import { Box, Flex, Text } from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import { useMemo, useRef, useState } from "react";
import * as Yup from "yup";

import { Heading } from "components/Heading";
import { FlashError } from "components/FormInputs/FlashError";
import { Submit } from "components/FormInputs/Submit";
import { OtpInput } from "newui/components/ui/OtpInput";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";

import { FormikAcceptTermsCheckbox } from "./Login/FormikAcceptTermsCheckbox";
import {
	TwoFactorChallengePayload,
	useTwoFactorChallenge,
} from "../hooks/useTwoFactorChallenge";
import type {
	AuthenticateResponse,
	TwoFactorMethod,
} from "../types/AuthenticateResponse.type";

const CODE_LENGTH = 6;

/**
 * Generic, non-enumerating copy. The backend never echoes the submitted code or
 * an attempt counter (M2); the FE mirrors that — a wrong TOTP and a wrong
 * recovery code surface the same message.
 */
const GENERIC_INVALID_MESSAGE =
	"That code was not accepted. Check the code and try again.";
const GENERIC_LOCKOUT_MESSAGE =
	"Too many attempts. Please wait a moment and try again.";

interface TwoFactorChallengeValues {
	code: string;
	recovery_code: string;
	remember_device: boolean;
}

interface TwoFactorChallengeProps {
	/** Server-advertised factors; defaults to TOTP when absent. */
	availableMethods?: TwoFactorMethod[];
	/** Opaque pending-login token from the credentials step. State-only. */
	mfaToken: string;
	/** Called when the pending session has expired — return to credentials. */
	onExpired: () => void;
	/** Called with the full authenticated response once the challenge clears. */
	onVerified: (response: AuthenticateResponse) => void | Promise<void>;
}

// Primary signal: the stable `error_code` the BE now attaches to every 2FA
// 422 (PRO-625 Task 009 contract hardening). The message-text match is kept
// only as a fallback for responses that predate the code (defensive, not the
// steady-state path).
const isExpiredError = (
	errorCode: unknown,
	message: unknown
): boolean =>
	errorCode === "TWO_FACTOR_CHALLENGE_EXPIRED" ||
	(typeof message === "string" && /expired|sign in again|took too long|verify your account/i.test(message));

const buildLockoutMessage = (retryAfterSeconds: number): string => {
	if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
		return GENERIC_LOCKOUT_MESSAGE;
	}
	const minutes = Math.ceil(retryAfterSeconds / 60);
	const unit = minutes === 1 ? "minute" : "minutes";
	return `Too many attempts. Please try again in ${minutes} ${unit}.`;
};

export const TwoFactorChallenge = ({
	mfaToken,
	availableMethods,
	onVerified,
	onExpired,
}: TwoFactorChallengeProps) => {
	const [isRecoveryMode, setIsRecoveryMode] = useState(false);
	const [challengeError, setChallengeError] = useState<string | null>(null);
	const codeFieldRef = useRef<HTMLDivElement>(null);

	const { mutateAsync, isPending } = useTwoFactorChallenge();

	// Method-agnostic: the primary factor is whatever the server advertises,
	// defaulting to TOTP. A recovery code is always available as a fallback and
	// is not itself an `available_methods` entry.
	const methods = availableMethods?.length ? availableMethods : ["totp"];
	const supportsTotp = methods.includes("totp");

	const validationSchema = useMemo(
		() =>
			isRecoveryMode
				? Yup.object({
					recovery_code: Yup.string()
						.trim()
						.required("Enter one of your recovery codes"),
				})
				: Yup.object({
					code: Yup.string()
						.matches(
							new RegExp(`^\\d{${CODE_LENGTH}}$`),
							`Enter the ${CODE_LENGTH}-digit code`
						)
						.required(),
				}),
		[isRecoveryMode]
	);

	const focusFirstCodeCell = () => {
		const firstCell = codeFieldRef.current?.querySelector<HTMLInputElement>(
			'[data-testid="otp-input-0"]'
		);
		firstCell?.focus();
	};

	const handleChallengeError = (
		error: unknown,
		helpers: FormikHelpers<TwoFactorChallengeValues>
	) => {
		const response = (error as { response?: { data?: { error?: unknown; error_code?: unknown; retry_after?: unknown }; headers?: Record<string, string>; status?: number; } })?.response;
		const status = response?.status;

		if (status === 429) {
			const retryAfterHeader = Number(response?.headers?.["retry-after"]);
			// `retry_after` (body) is the robust fallback when a proxy strips the
			// `Retry-After` header (PRO-625 Task 009 hardening item).
			const retryAfter = Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
				? retryAfterHeader
				: Number(response?.data?.retry_after);
			setChallengeError(buildLockoutMessage(retryAfter));
			return;
		}

		// The pending token has expired/vanished — send the user back to the
		// credentials step rather than re-prompting for a code they can't clear.
		if (status === 422 && isExpiredError(response?.data?.error_code, response?.data?.error)) {
			onExpired();
			return;
		}

		setChallengeError(GENERIC_INVALID_MESSAGE);

		// Clear and refocus so the next attempt is one keystroke away.
		if (isRecoveryMode) {
			helpers.setFieldValue("recovery_code", "");
		} else {
			helpers.setFieldValue("code", "");
			focusFirstCodeCell();
		}
	};

	const handleSubmit = async (
		values: TwoFactorChallengeValues,
		helpers: FormikHelpers<TwoFactorChallengeValues>
	) => {
		setChallengeError(null);

		const payload: TwoFactorChallengePayload = {
			mfa_token: mfaToken,
			remember_device: values.remember_device,
		};
		if (isRecoveryMode) {
			payload.recovery_code = values.recovery_code.trim();
		} else {
			payload.code = values.code;
		}

		try {
			const response = await mutateAsync(payload);
			await onVerified(response);
		} catch (error) {
			handleChallengeError(error, helpers);
		}
	};

	const toggleRecoveryMode = (
		helpers: Pick<FormikHelpers<TwoFactorChallengeValues>, "setFieldValue">
	) => {
		setChallengeError(null);
		helpers.setFieldValue("code", "");
		helpers.setFieldValue("recovery_code", "");
		setIsRecoveryMode((previous) => !previous);
	};

	return (
		<Box>
			<Heading className="pb-2 text-2xl font-bold text-gray-800">
				Two-step verification
			</Heading>
			<Text className="mb-5 text-sm text-gray-500">
				{isRecoveryMode
					? "Enter one of your saved recovery codes to sign in. If you have lost your recovery codes, please contact your administrator to reset your two-factor authentication."
					: "Enter the 6-digit code from your authenticator app to finish signing in."}
			</Text>

			{challengeError && (
				<FlashError
					heading="Verification failed"
					text={challengeError}
					margin="0px 0px 20px 0px"
				/>
			)}

			<Formik<TwoFactorChallengeValues>
				initialValues={{ code: "", recovery_code: "", remember_device: false }}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, setFieldValue, isSubmitting }) => (
					<Form autoComplete="off">
						{isRecoveryMode ? (
							<div ref={codeFieldRef} data-testid="two-factor-code-input">
								<FormikInputWithLabel
									name="recovery_code"
									label="Recovery code"
									placeholder="xxxxxxxxxx-xxxxxxxxxx"
									autoComplete="off"
								/>
								{challengeError && < Text className="text-sm text-red-500">
									The entered recovery code, must not be one you have used before.
								</Text>}
							</div>
						) : (
							supportsTotp && (
								<div ref={codeFieldRef} data-testid="two-factor-code-input">
									<OtpInput
										value={values.code}
										onChange={(next) => setFieldValue("code", next)}
										digitCount={CODE_LENGTH}
										ariaLabel="One-time code"
										invalid={!!challengeError}
										disabled={isPending}
									/>
								</div>
							)
						)}

						<Flex className="mb-5 mt-4 justify-between">
							<FormikAcceptTermsCheckbox
								label="Trust this device"
								name="remember_device"
								testId="trust-this-device-checkbox"
								noWarning
							/>
						</Flex>

						<Submit
							type="submit"
							widthExpand
							isSubmitting={isPending || isSubmitting}
							text="Verify"
							submittingText="Verifying..."
							style={{ textTransform: "uppercase", lineHeight: "1.25" }}
							testId="verify-challenge-btn"
						/>

						<Box className="mt-5 text-center text-sm">
							<button
								type="button"
								data-testid="use-recovery-code-link"
								className="text-blue-500 underline"
								onClick={() => toggleRecoveryMode({ setFieldValue })}
							>
								{isRecoveryMode
									? "Use your authenticator app instead"
									: "Use a recovery code"}
							</button>
						</Box>
					</Form>
				)}
			</Formik>
		</Box >
	);
};
